import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPurchasesApi } from "../../http/carPurchases/api";
import ModalDialog from "../ui/ModalDialog";

interface DeleteCarPurchaseDialogProps {
  carPurchaseId: string;
  onClose: () => void;
}

const DeleteCarPurchaseDialog: FC<DeleteCarPurchaseDialogProps> = ({ carPurchaseId, onClose }) => {
  const i18n = useI18n();
  const carPurchaseApi = useCarPurchasesApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());

  const result = carPurchaseApi.getAdminCarPurchase(carPurchaseId, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  useEffect(() => {
    result.trigger();
  }, [carPurchaseId]);

  if (!data && !result.error) {
    return null;
  }

  const handleDelete = () => {
    setIsSubmitting(true);
    carPurchaseApi
      .deleteAdminCarPurchase(carPurchaseId)
      .then(onClose)
      .catch((err) => dispatch(showErrorNotification(i18n, err)))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <ModalDialog
      onClose={onClose}
      isSubmitting={isSubmitting}
      title={i18n.t.carOrders.delete}
      actions={[
        {
          title: i18n.t.common.cancel,
          type: "secondary",
          onClick: onClose,
        },
        {
          title: i18n.t.common.yes,
          onClick: handleDelete,
        },
      ]}
    >
      {i18n.t.carOrders.questions.delete}
      {data?.carPlanningId && <p>{i18n.t.carOrders.questions.deleteLinkedPlanning}</p>}
    </ModalDialog>
  );
};

export default DeleteCarPurchaseDialog;
